import type { MenuCategory } from "@/api/vendure/menu/menu.types";
import type {
  DeliveryMethod,
  MerchantResponse,
  ScheduleInterval,
  Venue,
} from "@/api/vendure/merchant/merchant.types";
import type { JsonLd } from "@/lib/seo.types";

const DELIVERY_METHOD_LABELS: Record<DeliveryMethod, string> = {
  delivery: "Delivery",
  takeaway: "Takeaway",
  dine_in: "Dine-in",
};

const DESCRIPTION_MAX_LENGTH = 160;

export function serializeJsonLd(data: JsonLd): string {
  return JSON.stringify(data).replace(/</g, "\\u003c");
}

export function absoluteUrl(origin: string, pathOrUrl: string): string {
  return new URL(pathOrUrl, origin).toString();
}

export function formatDeliveryMethods(methods: DeliveryMethod[]): string {
  return methods.map((m) => DELIVERY_METHOD_LABELS[m] ?? m).join(", ");
}

export function truncateDescription(text: string): string {
  const clean = text.replace(/\s+/g, " ").trim();
  if (clean.length <= DESCRIPTION_MAX_LENGTH) return clean;
  return `${clean.slice(0, DESCRIPTION_MAX_LENGTH - 1).trimEnd()}…`;
}

function toSchemaDay(day: string): string {
  const lower = day.toLowerCase();
  return `https://schema.org/${lower.charAt(0).toUpperCase()}${lower.slice(1)}`;
}

export function buildVenueTitle(venue: Venue): string {
  const methods = formatDeliveryMethods(venue.delivery_methods);
  const base = `${venue.name} — Order Online`;
  return methods ? `${base} | ${methods}` : base;
}

export function buildVenueDescription(venue: Venue): string {
  if (venue.description) return truncateDescription(venue.description);
  const methods = formatDeliveryMethods(venue.delivery_methods);
  return truncateDescription(
    [
      venue.address
        ? `Order from ${venue.name} at ${venue.address}.`
        : `Order from ${venue.name}.`,
      methods ? `${methods} available.` : "",
      "Browse the menu and order online.",
    ].join(" "),
  );
}

export function buildOpeningHours(schedule: ScheduleInterval[]) {
  return schedule.map((interval) => ({
    "@type": "OpeningHoursSpecification",
    dayOfWeek: toSchemaDay(interval.opening_day),
    opens: interval.opening_time,
    closes: interval.closing_time,
  }));
}

function buildMenuSection(category: MenuCategory, currency: string): JsonLd {
  const items = category.products
    .filter((p) => p.enabled)
    .sort((a, b) => a.rank - b.rank)
    .map((product) => ({
      "@type": "MenuItem",
      name: product.name,
      ...(product.description ? { description: product.description } : {}),
      ...(product.images ? { image: product.images } : {}),
      offers: {
        "@type": "Offer",
        price: product.price.toFixed(2),
        priceCurrency: currency,
      },
    }));
  const subsections = category.subcategories
    .filter((c) => c.enabled)
    .sort((a, b) => a.rank - b.rank)
    .map((sub) => buildMenuSection(sub, currency));

  return {
    "@type": "MenuSection",
    name: category.name,
    ...(category.description ? { description: category.description } : {}),
    ...(items.length > 0 ? { hasMenuItem: items } : {}),
    ...(subsections.length > 0 ? { hasMenuSection: subsections } : {}),
  };
}

export function buildMenuJsonLd(categories: MenuCategory[], currency: string) {
  return {
    "@type": "Menu",
    hasMenuSection: categories
      .filter((c) => c.enabled)
      .sort((a, b) => a.rank - b.rank)
      .map((category) => buildMenuSection(category, currency)),
  };
}

export function buildRestaurantJsonLd({
  venue,
  url,
  image,
  menu,
}: {
  venue: Venue;
  url: string;
  image: string;
  menu?: MenuCategory[];
}) {
  const { name, description, address, latitude, longitude, phone, email } =
    venue;
  const currency = venue.currency ?? "EUR";

  return {
    "@context": "https://schema.org",
    "@type": "Restaurant",
    "@id": url,
    name,
    url,
    image,
    ...(description ? { description } : {}),
    ...(phone ? { telephone: phone } : {}),
    ...(email ? { email } : {}),
    currenciesAccepted: currency,
    ...(address
      ? { address: { "@type": "PostalAddress", streetAddress: address } }
      : {}),
    ...(latitude && longitude
      ? {
          geo: {
            "@type": "GeoCoordinates",
            latitude,
            longitude,
          },
        }
      : {}),
    ...(venue.schedule.length > 0
      ? { openingHoursSpecification: buildOpeningHours(venue.schedule) }
      : {}),
    ...(menu && menu.length > 0
      ? { hasMenu: buildMenuJsonLd(menu, currency) }
      : {}),
    potentialAction: {
      "@type": "OrderAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: url,
        actionPlatform: [
          "https://schema.org/DesktopWebPlatform",
          "https://schema.org/MobileWebPlatform",
        ],
      },
    },
  };
}

export function buildBrandJsonLd({
  brand,
  origin,
  url,
}: {
  brand: MerchantResponse;
  origin: string;
  url: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": url,
    name: brand.name,
    url,
    subOrganization: brand.venues.map((venue) => ({
      "@type": "Restaurant",
      name: venue.name,
      url: absoluteUrl(origin, `/business/${venue.token}`),
      ...(venue.image_url ? { image: venue.image_url } : {}),
      ...(venue.address
        ? {
            address: { "@type": "PostalAddress", streetAddress: venue.address },
          }
        : {}),
    })),
  };
}

export function noindexHead() {
  return { meta: [{ name: "robots", content: "noindex, follow" }] };
}

export const SITE_NAME = "Munchi";

export function buildWebSiteJsonLd(url: string): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url,
  };
}
